import React, { useEffect } from 'react';
import {
  HttpError,
  IResourceComponentsProps,
  useNavigation,
  useTranslate,
  useUpdate,
} from '@pankod/refine-core';
import {
  Create,
  Box,
  FormControl,
  FormHelperText,
  Grid,
  Stack,
  TextField,
  Typography,
  Backdrop,
  CircularProgress,
} from '@pankod/refine-mui';
import { useStepsForm } from '@pankod/refine-react-hook-form';
import { IUpdateUserPoints } from 'interfaces';
import _ from 'lodash';
import { firestoreDatabase } from 'lib/livenowFirebaseService';
import { useSearchParams } from '@pankod/refine-react-router-v6';

export const UserPointCreate: React.FC<
  IResourceComponentsProps
> = () => {
  const t = useTranslate();
  const { mutate: mutateUpdate } = useUpdate();
  const { push } = useNavigation();
  // get uid from query in url
  const [searchParams] = useSearchParams();
  const uid = searchParams.get('uid') || '';

  const {
    refineCore: { onFinish, formLoading },
    register,
    setValue,
    getValues,
    setError,
    formState: { errors },
  } = useStepsForm<IUpdateUserPoints, HttpError, IUpdateUserPoints>({
    stepsProps: {
      isBackValidate: false,
    },
    warnWhenUnsavedChanges: true,
    refineCoreProps: {
      redirect: false,
    }
  });

  const handleSubmit = async () => {
    const now = new Date();
    const point = +getValues('points');
    const uid = getValues('uid');

    if (isNaN(point) || !point) {
      setError('points', {
        type: 'manual',
        message: 'Point must be a number != 0',
      });
      return;
    }

    const type = point > 0 ? 'ap' : 'sp';

    const data = {
      ...getValues(),
      point,
      type,
      created_at: now,
      updated_at: now,
    };
    const response = await firestoreDatabase.getOne({
      resource: 'users',
      id: uid,
    });
    const { data: user } = response;
    if (!user?.uid) {
      setError('uid', {
        type: 'manual',
        message: 'User not found',
      });
      return;
    }
    user.points = user.points || 0;

    await onFinish(data);
    await mutateUpdate({
      resource: 'users',
      id: uid,
      dataProviderName: 'livenow',
      values: {
        // increment the user's points
        points: user.points + point,
      },
    });

    push(`/users/show/${uid}`);
  };

  const renderForm = () => {
    return (
      <>
        <Grid
          container
          sx={{
            marginX: { xs: '0px' },
          }}
        >
          <Grid item xs={12}>
            <Grid container>
              <Grid item paddingX={4} xs={12}>
                <Stack gap="24px">
                  <FormControl>
                    <TextField
                      {...register('points', {
                        required: t('errors.required.field', {
                          field: 'name',
                        }),
                        maxLength: {
                          value: 100,
                          message: t('errors.required.max', {
                            max: 100,
                          }),
                        },
                      })}
                      size="medium"
                      variant="outlined"
                      required
                      label="Point (required)"
                      type="number"
                    />
                    {errors.points && (
                      <FormHelperText error>
                        {errors.points.message}
                      </FormHelperText>
                    )}
                  </FormControl>

                  <FormControl>
                    <TextField
                      {...register('reason_type', {
                        required: t('errors.required.field', {
                          field: 'reason_type',
                        }),
                        maxLength: {
                          value: 100,
                          message: t('errors.required.max', {
                            max: 100,
                          }),
                        },
                      })}
                      size="medium"
                      variant="outlined"
                      required
                      label="Reason  Type (required)"
                    />
                    {errors.reason_type && (
                      <FormHelperText error>
                        {errors.reason_type.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  };

  useEffect(() => {
    if (!uid) {
      push('/users');
      return;
    }
    setValue('uid', uid); 
  }, [uid]);

  return (
    <Create
      title={<Typography variant="h5">Create User Point</Typography>}
      isLoading={formLoading}
      saveButtonProps={{ onClick: handleSubmit }}
    >
      <Box
        component="form"
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
        autoComplete="off"
      >
        <br />
        {renderForm()}
        <Backdrop
          sx={{
            color: '#fff',
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
          open={formLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Box>
    </Create>
  );
};
